import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import Switch from '@mui/material/Switch';
import { useWallet } from '@solana/wallet-adapter-react';
import { VersionedTransaction } from '@solana/web3.js';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createUpdateLevelRatios, createUpdateMinSignupAmount, createUpdateMintingEnabled, createUpdateTokenRatio, getWithdrawTransaction, submitTransaction } from '../api/bot.api';
import { MAX_LEVEL_LENGTH } from '../libs/constants';
import { getConnection, isValidSolanaAddress, shortenAddress } from '../libs/utils';
import { setSelectedMenuAction, setSettingsAction, setSnackMsgAction } from '../store/slice';
import CopyButton from './CopyButton';
import NumberField from './NumberField';
import { MenuInterface } from './types';


export default function AdminContainer() {
    const { sendTransaction } = useWallet();
    const dispatch = useDispatch();
    const settings = useSelector((state: any) => state.settings);
    const [levelRatios, setLevelRatios] = useState<string>("");
    const [withdrawDestination, setWithdrawDestination] = useState<string>("");
    const [withdrawAmount, setWithdrawAmount] = useState<string>("");
    const [withdrawDestinationError, setWithdrawDestinationError] = useState<boolean>(false);
    const [withdrawAmountError, setWithdrawAmountError] = useState<boolean>(false);
    const [isUpdatingLevelRatios, setIsUpdatingLevelRatios] = useState<boolean>(false);
    const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);
    const [minSignupAmount, setMinSignupAmount] = useState<string>("");
    const [isUpdatingMinSignupAmount, setIsUpdatingMinSignupAmount] = useState<boolean>(false);
    const [tokenRatio, setTokenRatio] = useState<string>("");
    const [isUpdatingTokenRatio, setIsUpdatingTokenRatio] = useState<boolean>(false);

    useEffect(() => {
        dispatch(setSelectedMenuAction(MenuInterface.ADMIN));
    }, []);

    useEffect(() => {
        if(withdrawDestination === "") {
            setWithdrawDestinationError(false);
        } else {
            if(!isValidSolanaAddress(withdrawDestination)) {
                setWithdrawDestinationError(true);
            } else {
                setWithdrawDestinationError(false);
            }
        }
        if(withdrawAmount === "") {
            setWithdrawAmountError(false);
        } else {
            if(Number(withdrawAmount) <= 0) {
                setWithdrawAmountError(true);
            } else {
                setWithdrawAmountError(false);
            }
        }
    }, [withdrawDestination, withdrawAmount]);

    const handleMintingEnabledChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        const res = await createUpdateMintingEnabled({enabled: value});
        if (!res.success) {
            toast.error(res.msg);
            return;
        }
        const { signedTx } = res.data;
        if (!signedTx) {
            toast.error("Failed to create minting enabled transaction");
            return;
        }
        const tx = VersionedTransaction.deserialize(new Uint8Array(Buffer.from(signedTx, 'base64')));
        // Sign the transaction
        if (!sendTransaction) {
            toast.error("Failed to sign transaction");
            return;
        }
        const signature = await sendTransaction(tx, getConnection());
        if (!signature) {
            toast.error("Failed to sign transaction");
            return;
        }
        dispatch(setSnackMsgAction("Transaction submitted. Please wait for confirmation."));
        const submitRes = await submitTransaction({ signature });
        dispatch(setSnackMsgAction(""));
        if (!submitRes.success) {
            toast.error(submitRes.msg);
            return;
        }
        dispatch(setSettingsAction({...settings, mintingEnabled: value}));
        toast.success(`Minting is ${value ? "Enabled" : "Disabled"}`);
    };

    const handleUpdateLevelRatios = async () => {
        const levelRatiosArray = levelRatios.split(" ").map(ratio => Math.floor(Number(ratio) * 10));
        const filteredRatios = levelRatiosArray.filter(ratio => ratio && ratio !== 0);
        if (filteredRatios.length === 0) {
            toast.error("Level ratios is invalid");
            return;
        }
        if (filteredRatios.length > MAX_LEVEL_LENGTH) {
            toast.error(`Level ratios must be less than ${MAX_LEVEL_LENGTH}`);
            return;
        }
        setIsUpdatingLevelRatios(true);
        const res = await createUpdateLevelRatios({levelRatios: filteredRatios});
        setIsUpdatingLevelRatios(false);
        if (!res.success) { 
            toast.error(res.msg);
            return;
        }
        const { signedTx } = res.data;
        if (!signedTx) {
            toast.error("Failed to create level ratios transaction");
            return;
        }
        const tx = VersionedTransaction.deserialize(new Uint8Array(Buffer.from(signedTx, 'base64')));
        // Sign the transaction
        if (!sendTransaction) {
            toast.error("Failed to sign transaction");
            return;
        }
        const signature = await sendTransaction(tx, getConnection());
        if (!signature) {
            toast.error("Failed to sign transaction");
            return;
        }
        dispatch(setSnackMsgAction("Transaction submitted. Please wait for confirmation."));
        const submitRes = await submitTransaction({ signature });
        dispatch(setSnackMsgAction(""));
        if (!submitRes.success) {
            toast.error(submitRes.msg);
            return;
        }
        dispatch(setSettingsAction({...settings, levelRatios: filteredRatios}));
        toast.success("Level ratios updated successfully");
        
    };

    const handleWithdraw = async () => {
        if(withdrawDestinationError || withdrawAmountError ||withdrawDestination === '' || withdrawAmount === '') {
            toast.error("Withdraw destination or amount is invalid");
            return;
        }
        if(Number(withdrawAmount) > settings?.totalSol) {
            toast.error("Withdraw amount is greater than total invested");
            return;
        }
        setIsWithdrawing(true);
        const res = await getWithdrawTransaction({destination: withdrawDestination, amount: Number(withdrawAmount)});
        setIsWithdrawing(false);
        if (!res.success) {
            toast.error(res.msg);
            return;
        }
        const { signedTx, historyId } = res.data;
        if (!signedTx) {
            toast.error("Failed to create withdraw transaction");
            return;
        }
        const tx = VersionedTransaction.deserialize(new Uint8Array(Buffer.from(signedTx, 'base64')));
        // Sign the transaction
        if (!sendTransaction) {
            toast.error("Failed to sign transaction");
            return;
        }
        const signature = await sendTransaction(tx, getConnection());
        if (!signature) {
            toast.error("Failed to sign transaction");
            return;
        }
        dispatch(setSnackMsgAction("Transaction submitted. Please wait for confirmation."));
        const submitRes = await submitTransaction({ signature, historyId });
        dispatch(setSnackMsgAction(""));
        if (!submitRes.success) {
            toast.error(submitRes.msg);
            return;
        }
        toast.success("Withdraw transaction submitted successfully");
        
    }

    const handleUpdateMinSignupAmount = async () => {
        if (minSignupAmount === "" || Number(minSignupAmount) < 0) {
            toast.error("Min signup amount is invalid");
            return;
        }
        setIsUpdatingMinSignupAmount(true);
        const res = await createUpdateMinSignupAmount({minSignupAmount: Number(minSignupAmount)});
        setIsUpdatingMinSignupAmount(false);
        if (!res.success) { 
            toast.error(res.msg);
            return;
        }
        const { signedTx } = res.data;
        if (!signedTx) {
            toast.error("Failed to create min signup amount transaction");
            return;
        }
        const tx = VersionedTransaction.deserialize(new Uint8Array(Buffer.from(signedTx, 'base64')));
        // Sign the transaction
        if (!sendTransaction) {
            toast.error("Failed to send transaction");
            return;
        }
        const signature = await sendTransaction(tx, getConnection());
        if (!signature) {
            toast.error("Failed to send transaction");
            return;
        }
        dispatch(setSnackMsgAction("Transaction submitted. Please wait for confirmation."));
        const submitRes = await submitTransaction({ signature });
        dispatch(setSnackMsgAction(""));
        if (!submitRes.success) {
            toast.error(submitRes.msg);
            return;
        }
        dispatch(setSettingsAction({...settings, minSignupAmount: Number(minSignupAmount)}));
        toast.success("Min signup amount updated successfully");
    }

    const handleUpdateTokenRatio = async () => {
        if (Number(tokenRatio) <= 0) {
            toast.error("Token ratio is invalid");
            return;
        }
        const tempRatio = Math.floor(Number(tokenRatio) * 1000) / 1000;
        setIsUpdatingTokenRatio(true);
        const res = await createUpdateTokenRatio({tokenRatio: tempRatio});
        setIsUpdatingTokenRatio(false);
        if (!res.success) { 
            toast.error(res.msg);
            return;
        }
        const { signedTx } = res.data;
        if (!signedTx) {
            toast.error("Failed to create token ratio transaction");
            return;
        }
        const tx = VersionedTransaction.deserialize(new Uint8Array(Buffer.from(signedTx, 'base64')));
        // Sign the transaction
        if (!sendTransaction) {
            toast.error("Failed to sign transaction");
            return;
        }
        const signature = await sendTransaction(tx, getConnection());
        if (!signature) {
            toast.error("Failed to sign transaction");
            return;
        }
        dispatch(setSnackMsgAction("Transaction submitted. Please wait for confirmation."));
        const submitRes = await submitTransaction({ signature });
        dispatch(setSnackMsgAction(""));
        if (!submitRes.success) {
            toast.error(submitRes.msg);
            return;
        }
        dispatch(setSettingsAction({...settings, tokenRatio: tempRatio}));
        toast.success("Token ratio updated successfully");
    }

    return (
        <Box display="flex" flexDirection="column" alignItems="center"
            sx={{ borderRight: {xs: "none", md: '1px solid #1e1e1e'}, overflowY:'auto', pt: 0, pb: 0}}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} 
                    sx={{ pt: {md: 3, xs: 1}, pb: 4, width: {md: 320, xs: '100%'}, maxWidth: {md: 320, xs: '450px'}, minWidth: {md: 320}, px: {md:2, sm: 6, xs: 2}, border: {md: "none", xs: '1px solid #1e1e1e'}}}>
                <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end" gap={2} sx={{width: '100%'}}>
                    <Typography variant="h6" color="text.secondary" sx={{display: {md: 'block', xs: 'none'}}}>Settings</Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between"  gap={2} sx={{width: '100%'}}>
                    <Typography variant="subtitle1">USDC Vault</Typography>
                    <Typography variant="h6">{settings?.totalUsdc.toFixed(2)}
                        <Typography variant='caption' color='text.secondary'> USDC</Typography>
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{width: '100%'}}>
                    <Typography variant="subtitle1">Signup Address</Typography>
                    <Box display="flex" alignItems="center" sx={{}}>
                        <Typography variant="subtitle2">{shortenAddress(settings?.rootSignupAddress, 4)}</Typography>
                        <CopyButton text={settings?.rootSignupAddress}/>
                    </Box>
                </Box>

                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{width: '100%'}}>
                    <Typography variant="subtitle1">USDC Token</Typography>
                    <Box display="flex" alignItems="center" sx={{}}>
                        <Typography variant="subtitle2">{shortenAddress(settings?.usdcMint, 4)}</Typography>
                        <CopyButton text={settings?.usdcMint}/>
                    </Box>
                </Box>
                
                <Divider sx={{width: '100%'}}/>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{width: '100%'}}>
                    <Typography variant="subtitle1">Minting is {settings?.mintingEnabled ? "Enabled" : "Disabled"}</Typography>
                    <Switch checked={settings?.mintingEnabled} onChange={handleMintingEnabledChange}/>
                </Box>
                <Divider sx={{width: '100%'}}/>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} sx={{width: '100%'}}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{width: '100%'}}>
                        <Typography variant="subtitle1">Min Signup Amount</Typography>
                        <Typography variant="h6">{settings?.minSignupAmount.toFixed(1)}
                            <Typography variant='caption' color='text.secondary'> USDC</Typography>
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2} sx={{width: '100%'}}>
                        <NumberField label="New Min" placeholder="50" sx={{width: 1}} value={minSignupAmount} onChange={(value) => setMinSignupAmount(value)}/>
                        <Button variant="outlined" color="primary" sx={{minWidth: 100}} onClick={handleUpdateMinSignupAmount} loading={isUpdatingMinSignupAmount} loadingIndicator="Updating…">Update</Button> 
                    </Box>
                </Box>
                <Divider sx={{width: '100%'}}/>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} sx={{width: '100%'}}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{width: '100%'}}>
                        <Typography variant="subtitle1">SMRTY Value</Typography>
                        <Typography variant="h6">{settings?.tokenRatio.toFixed(2)}
                            <Typography variant='caption' color='text.secondary'> USDC</Typography>
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2} sx={{width: '100%'}}>
                        <NumberField label="New Value" placeholder="1.5" sx={{width: 1}} value={tokenRatio} onChange={(value) => setTokenRatio(value)}/>
                        <Button variant="outlined" color="primary" sx={{minWidth: 100}} onClick={handleUpdateTokenRatio} loading={isUpdatingTokenRatio} loadingIndicator="Updating…">Update</Button> 
                    </Box>
                </Box>
                <Divider sx={{width: '100%'}}/>
                <Box display="flex" alignItems="center" flexDirection="column" gap={2} sx={{width: '100%'}}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{width: '100%'}}>
                        <Typography variant="subtitle1">Level Ratios</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            {settings?.levelRatios.map((ratio: number, index: number) => (
                                <React.Fragment key={index}>
                                    <Typography variant="h6">{(ratio / 10)}</Typography>
                                    {index < settings.levelRatios.length - 1 && <Divider orientation="vertical" flexItem sx={{height: 20, alignSelf: 'center'}} />}
                                </React.Fragment>
                            ))}
                            <Typography variant="caption" color="text.secondary" sx={{pt: 1}}>%</Typography>
                        </Box>
                    </Box>
                    <TextField label="New Level Ratios" size="small" variant="outlined" placeholder="10 5 3 2" sx={{width: 1}} value={levelRatios} onChange={(e) => setLevelRatios(e.target.value)}/>
                    <Button variant="outlined" color="primary" sx={{minWidth: 200}} onClick={handleUpdateLevelRatios} loading={isUpdatingLevelRatios} loadingIndicator="Updating…">Update Level Ratios</Button> 
                </Box>
                <Divider sx={{width: '100%'}}/>
                
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="space-between" gap={2} sx={{width: '100%'}}>
                    <Typography variant="subtitle1">Withdraw</Typography>
                    <TextField error={withdrawDestinationError} multiline rows={3} label="Destination Address" size="small" variant="outlined" sx={{width: 1}} value={withdrawDestination} onChange={(e) => setWithdrawDestination(e.target.value)}/>
                    <NumberField helperText={withdrawAmountError ? "Amount is invalid" : ""} label="USDC Amount" sx={{width: 1}} value={withdrawAmount} onChange={(value) => setWithdrawAmount(value)}/>
                    <Button variant="contained" color="secondary" sx={{minWidth: 200}}  onClick={handleWithdraw} loading={isWithdrawing} loadingIndicator="Withdrawing…">Withdraw</Button>
                </Box>
            </Box>
        </Box>
    );
}